import { useMemo } from "react";
import { getImageUrl } from "../helper/image";
import { logItemSelect } from "../api/events";
import ProductName from "./ProductName";
import DeferredImage from "./imgs/DeferredImage";

const POTD_DESCRIPTIONS = [
  "Hand-picked from the vault — a standout piece collectors are watching right now.",
  "Fresh on the radar: sharp corners, bold artwork, and serious shelf appeal.",
  "Today's spotlight — rare enough to notice, accessible enough to grab.",
  "A fan-favourite that keeps selling out. Don't sleep on this one.",
  "From the latest arrivals pile — premium look, collector-grade vibes.",
  "The card everyone stops scrolling for. Yours could be next.",
  "Clean, crisp, and ready for a new home in your collection.",
  "Limited attention, unlimited hype — our pick of the day.",
];

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function useProductOfTheDay(products, slotIndex = 0) {
  return useMemo(() => {
    const pool = (products || []).filter((p) => p?.id && p?.name);
    if (pool.length === 0) return { pick: null, blurb: "" };

    const dayKey = new Date().toISOString().slice(0, 10);
    const ranked = [...pool].sort((a, b) => {
      const ha = hashString(`${dayKey}|rank|${a.id}`);
      const hb = hashString(`${dayKey}|rank|${b.id}`);
      return ha - hb;
    });
    const blurbIndex =
      hashString(`${dayKey}|potd-desc|${slotIndex}`) % POTD_DESCRIPTIONS.length;

    return {
      pick: ranked[slotIndex % ranked.length],
      blurb: POTD_DESCRIPTIONS[blurbIndex],
    };
  }, [products, slotIndex]);
}

export default function ProductOfTheDay({
  products = [],
  setSelectedItem,
  variant = "hero",
  showDivider = true,
  slotIndex = 0,
}) {
  const { pick, blurb } = useProductOfTheDay(products, slotIndex);

  if (!pick) return null;

  const handleClick = () => {
    if (setSelectedItem) {
      setSelectedItem(pick);
      logItemSelect(String(pick.id), "product-of-the-day");
    }
  };

  return (
    <section
      className={`product-of-the-day product-of-the-day--${variant}`}
    >
      <p className="product-of-the-day-label">Product of the Day</p>
      <div className="product-of-the-day-card">
        <div className="product-of-the-day-image-wrap shiny radius">
          <DeferredImage
            className="radius"
            src={getImageUrl(pick, true)}
            alt={pick.name}
            loading="lazy"
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleClick();
            }}
          />
        </div>
        <div className="product-of-the-day-copy">
          <h2 className="product-of-the-day-name">
            <ProductName name={pick.name} type={pick.type} useFullName stacked />
          </h2>
          {pick.setName && (
            <p className="product-of-the-day-set">{pick.setName}</p>
          )}
          <p className="product-of-the-day-desc">{blurb}</p>
          <span className="product-of-the-day-cta">View details</span>
        </div>
      </div>
      {showDivider && variant === "hero" && <div className="section-divider" />}
    </section>
  );
}
